/**
 * Class Flow — timed sprints on inSessionRooms.classFlowSprint
 */

import { db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment,
  deleteField,
} from 'firebase/firestore';
import type { UpdateData, DocumentData } from 'firebase/firestore';
import type { ClassFlowSprintState, EnergyType } from '../types/season1';
import { addVaultPpGrantedMidSessionStat, trackParticipation } from './inSessionStatsService';
import { isGlobalHost } from './inSessionService';
import { mirrorProfileXpToProgressionSystems } from './playerProgressionRewards';
import { applyPlayerPPDelta } from './playerPowerPoints';
import { recordSprintMarkedCompleteForPlayer, recordSprintOpportunityForPlayers } from './weeklyGoalsService';
import {
  recordHabitLiveEventSprintCompletion,
  recordHabitLiveEventSprintOpportunity,
} from './habitLiveEventEvidenceService';
import {
  recordClassFlowSprintJoinsForPlayers,
  recordClassFlowSprintCompletion,
  recordClassFlowSprintMissed,
} from './productivityTracking';
import { liveEventAwardDebug, truncateId } from './liveEventDebugLogging';
import { fetchClassroomStudentUids } from './classFlowSprintRosterService';
import type { CfSprintAssignment } from '../types/classFlowWork';
import {
  extendedEndsAtMs,
  imageField,
  parseSprintAssignments,
  resumedEndsAtMs,
  type CfTimerInput,
} from './classFlowConfig';

const roomRef = (sessionId: string) => doc(db, 'inSessionRooms', sessionId);

/** Human-readable duration for battle log (stored value is always seconds). */
export function formatSprintDurationForLog(durationSeconds: number): string {
  const s = Math.max(0, Math.floor(durationSeconds || 0));
  const wholeMin = Math.floor(s / 60);
  const rem = s % 60;
  if (wholeMin === 0) return `${s}s`;
  if (rem === 0) return wholeMin === 1 ? '1 minute' : `${wholeMin} minutes`;
  return `${wholeMin} min ${rem}s`;
}

function canActAsRoomHost(
  roomHostUid: unknown,
  actingUid: string,
  email?: string,
  displayName?: string
): boolean {
  if (typeof roomHostUid === 'string' && roomHostUid === actingUid) return true;
  return isGlobalHost(actingUid, email, displayName);
}

function tsToMillis(t: unknown): number | null {
  if (t && typeof (t as Timestamp).toMillis === 'function') {
    return (t as Timestamp).toMillis();
  }
  if (t instanceof Date) return t.getTime();
  return null;
}

/** Parse Firestore map into a typed sprint (or null if missing/invalid). */
export function parseClassFlowSprint(raw: unknown): ClassFlowSprintState | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.title !== 'string') return null;
  if (typeof o.hostUid !== 'string') return null;
  if (o.status !== 'live' && o.status !== 'closed') return null;
  const startedMs = tsToMillis(o.startedAt);
  const endsMs = tsToMillis(o.endsAt);
  if (startedMs == null || endsMs == null) return null;

  const marked = Array.isArray(o.markedCompleteUids)
    ? o.markedCompleteUids.filter((x): x is string => typeof x === 'string')
    : [];
  const granted = Array.isArray(o.rewardsGrantedUids)
    ? o.rewardsGrantedUids.filter((x): x is string => typeof x === 'string')
    : [];
  const penaltyGranted = Array.isArray(o.incompletePenaltiesGrantedUids)
    ? o.incompletePenaltiesGrantedUids.filter((x): x is string => typeof x === 'string')
    : [];

  return {
    id: o.id,
    title: o.title,
    description: typeof o.description === 'string' ? o.description : undefined,
    durationSeconds: typeof o.durationSeconds === 'number' ? o.durationSeconds : 0,
    startedAt: (o.startedAt as Timestamp) || new Date(startedMs),
    endsAt: (o.endsAt as Timestamp) || new Date(endsMs),
    status: o.status,
    hostUid: o.hostUid,
    rewardParticipationPoints:
      typeof o.rewardParticipationPoints === 'number' ? Math.max(1, Math.floor(o.rewardParticipationPoints)) : 1,
    rewardVaultPP: typeof o.rewardVaultPP === 'number' ? Math.max(0, Math.floor(o.rewardVaultPP)) : 0,
    rewardXP: typeof o.rewardXP === 'number' ? Math.max(0, Math.floor(o.rewardXP)) : 0,
    incompletePenaltyVaultPP:
      typeof o.incompletePenaltyVaultPP === 'number' ? Math.max(0, Math.floor(o.incompletePenaltyVaultPP)) : 0,
    markedCompleteUids: marked,
    rewardsGrantedUids: granted,
    incompletePenaltiesGrantedUids: penaltyGranted,
    pausedAt: tsToMillis(o.pausedAt) != null ? (o.pausedAt as Timestamp) : null,
    pausedAccumMs: typeof o.pausedAccumMs === 'number' ? Math.max(0, o.pausedAccumMs) : 0,
    extendedSeconds: typeof o.extendedSeconds === 'number' ? Math.max(0, o.extendedSeconds) : 0,
    objective: typeof o.objective === 'string' ? o.objective : undefined,
    planId: typeof o.planId === 'string' ? o.planId : null,
    assignments: parseSprintAssignments(o.assignments),
    ...imageField(o.imageUrl as string | undefined),
  };
}

/** Shared timer view of a sprint (all clients derive the countdown from the same timestamps). */
export function sprintTimerInput(sprint: ClassFlowSprintState): CfTimerInput {
  return {
    status: sprint.status,
    startedAtMs: tsToMillis(sprint.startedAt) ?? 0,
    endsAtMs: tsToMillis(sprint.endsAt) ?? 0,
    pausedAtMs: tsToMillis(sprint.pausedAt),
  };
}

export type StartClassFlowSprintPayload = {
  title: string;
  description?: string;
  durationSeconds: number;
  rewardParticipationPoints: number;
  rewardVaultPP: number;
  rewardXP: number;
  /** Deduct this much vault PP from each session player not marked complete (0 = off). */
  incompletePenaltyVaultPP?: number;
  /** Class Flow Sprint Management (optional). */
  objective?: string;
  planId?: string | null;
  assignments?: CfSprintAssignment[];
  imageUrl?: string;
  /** Server-corrected clock so every client sees the same countdown. */
  nowMs?: number;
};

export async function startClassFlowSprint(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined,
  payload: StartClassFlowSprintPayload
): Promise<{ ok: boolean; error?: string; sprintId?: string }> {
  try {
    const ref = roomRef(sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ok: false, error: 'Session not found' };
    const players = (snap.data()?.players as { userId?: string }[]) || [];
    let playerUidsForSprints = players
      .map((p) => p.userId)
      .filter((x): x is string => typeof x === 'string' && !!x);
    const hostUid = snap.data()?.hostUid;
    if (!canActAsRoomHost(hostUid, actingUid, actingEmail, actingDisplayName)) {
      return { ok: false, error: 'Only the session host can start a sprint' };
    }

    const title = payload.title.trim();
    if (!title) return { ok: false, error: 'Enter a sprint goal title' };

    const maxDuration = payload.assignments?.length ? 7200 : 3600;
    const durationSeconds = Math.max(60, Math.min(maxDuration, Math.floor(payload.durationSeconds || 120)));
    const rewardParticipationPoints = Math.max(1, Math.min(20, Math.floor(payload.rewardParticipationPoints || 1)));
    const rewardVaultPP = Math.max(0, Math.min(5000, Math.floor(payload.rewardVaultPP || 0)));
    const rewardXP = Math.max(0, Math.min(5000, Math.floor(payload.rewardXP || 0)));
    const incompletePenaltyVaultPP = Math.max(
      0,
      Math.min(5000, Math.floor(payload.incompletePenaltyVaultPP ?? 0))
    );

    const now = payload.nowMs ? Timestamp.fromMillis(payload.nowMs) : Timestamp.now();
    const endsAt = Timestamp.fromMillis(now.toMillis() + durationSeconds * 1000);
    const id = `sprint_${Date.now()}`;
    const workFields: Record<string, unknown> = {};
    if (payload.assignments?.length || payload.objective || payload.planId) {
      workFields.objective = (payload.objective || '').trim();
      workFields.planId = payload.planId ?? null;
      workFields.assignments = payload.assignments ?? [];
    }

    const roomClassId =
      typeof snap.data()?.classId === 'string'
        ? snap.data()!.classId
        : Array.isArray(snap.data()?.classIds) && typeof snap.data()!.classIds[0] === 'string'
          ? snap.data()!.classIds[0]
          : undefined;
    if (roomClassId) {
      try {
        const classUids = await fetchClassroomStudentUids(roomClassId);
        if (classUids.length > 0) {
          playerUidsForSprints = Array.from(new Set([...classUids, ...playerUidsForSprints]));
        }
      } catch {
        /* keep session-only roster if classroom read fails */
      }
    }

    await updateDoc(ref, {
      classFlowSprint: {
        id,
        title,
        description: (payload.description || '').trim() || '',
        durationSeconds,
        startedAt: now,
        endsAt,
        status: 'live',
        hostUid: actingUid,
        rewardParticipationPoints,
        rewardVaultPP,
        rewardXP,
        incompletePenaltyVaultPP,
        markedCompleteUids: [],
        rewardsGrantedUids: [],
        incompletePenaltiesGrantedUids: [],
        ...workFields,
        ...imageField(payload.imageUrl),
      },
      updatedAt: serverTimestamp(),
      battleLog: arrayUnion(
        `🏃 Sprint started: "${title}" — ${formatSprintDurationForLog(durationSeconds)} to complete the goal.`
      ),
    });

    void recordSprintOpportunityForPlayers(playerUidsForSprints, sessionId).catch(() => {
      /* best-effort weekly goals */
    });
    void recordHabitLiveEventSprintOpportunity(sessionId, title, playerUidsForSprints).catch(() => {
      /* best-effort habit live-event evidence */
    });
    const liveEventMode =
      typeof snap.data()?.liveEventMode === 'string' ? snap.data()!.liveEventMode : undefined;
    const neutralFlowEnergyType = snap.data()?.neutralFlowEnergyType as EnergyType | undefined;
    recordClassFlowSprintJoinsForPlayers(
      sessionId,
      id,
      playerUidsForSprints,
      roomClassId,
      title,
      now.toMillis(),
      { liveEventMode, neutralFlowEnergyType }
    );

    return { ok: true, sprintId: id };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

export async function closeClassFlowSprint(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined
): Promise<{ ok: boolean; error?: string }> {
  try {
    const ref = roomRef(sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ok: false, error: 'Session not found' };
    if (!canActAsRoomHost(snap.data()?.hostUid, actingUid, actingEmail, actingDisplayName)) {
      return { ok: false, error: 'Only the session host can close the sprint' };
    }
    const sprint = parseClassFlowSprint(snap.data()?.classFlowSprint);
    if (!sprint) return { ok: false, error: 'No active sprint' };

    await updateDoc(ref, {
      'classFlowSprint.status': 'closed',
      updatedAt: serverTimestamp(),
      battleLog: arrayUnion(`🏁 Sprint window closed: "${sprint.title}" — award completions when ready.`),
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

type SprintTimerAction =
  | { kind: 'pause' }
  | { kind: 'resume' }
  | { kind: 'extend'; seconds: number }
  | { kind: 'autoClose' };

/**
 * Pause / resume / extend / auto-close in a transaction so concurrent hosts cannot corrupt the
 * shared timestamps. `nowMs` should be server-corrected (see useServerClock).
 */
export async function updateClassFlowSprintTimer(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined,
  action: SprintTimerAction,
  nowMs: number = Date.now()
): Promise<{ ok: boolean; error?: string }> {
  try {
    const ref = roomRef(sessionId);
    let logLine: string | null = null;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Session not found');
      if (!canActAsRoomHost(snap.data()?.hostUid, actingUid, actingEmail, actingDisplayName)) {
        throw new Error('Only the session host can control the sprint timer');
      }
      const sprint = parseClassFlowSprint(snap.data()?.classFlowSprint);
      if (!sprint) throw new Error('No active sprint');
      const t = sprintTimerInput(sprint);
      const patch: Record<string, unknown> = {};
      if (action.kind === 'pause') {
        if (sprint.status !== 'live' || t.pausedAtMs != null) return;
        if (t.endsAtMs <= nowMs) return;
        patch['classFlowSprint.pausedAt'] = Timestamp.fromMillis(nowMs);
        logLine = `⏸️ Sprint paused: "${sprint.title}"`;
      } else if (action.kind === 'resume') {
        if (t.pausedAtMs == null) return;
        const r = resumedEndsAtMs(t, nowMs);
        patch['classFlowSprint.endsAt'] = Timestamp.fromMillis(r.endsAtMs);
        patch['classFlowSprint.pausedAt'] = deleteField();
        patch['classFlowSprint.pausedAccumMs'] = (sprint.pausedAccumMs || 0) + r.pausedMs;
        logLine = `▶️ Sprint resumed: "${sprint.title}"`;
      } else if (action.kind === 'extend') {
        const seconds = Math.max(15, Math.min(3600, Math.floor(action.seconds)));
        const live = { ...t, status: 'live' as const };
        patch['classFlowSprint.endsAt'] = Timestamp.fromMillis(extendedEndsAtMs(live, nowMs, seconds));
        patch['classFlowSprint.extendedSeconds'] = (sprint.extendedSeconds || 0) + seconds;
        if (sprint.status !== 'live') patch['classFlowSprint.status'] = 'live';
        logLine = `⏱️ Sprint extended by ${formatSprintDurationForLog(seconds)}: "${sprint.title}"`;
      } else {
        if (sprint.status !== 'live' || t.pausedAtMs != null || t.endsAtMs > nowMs) return;
        patch['classFlowSprint.status'] = 'closed';
        logLine = `🏁 Sprint time is up: "${sprint.title}" — submissions closed.`;
      }
      patch.updatedAt = serverTimestamp();
      if (logLine) patch.battleLog = arrayUnion(logLine);
      tx.update(ref, patch as DocumentData);
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export async function clearClassFlowSprint(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined
): Promise<{ ok: boolean; error?: string }> {
  try {
    const ref = roomRef(sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ok: false, error: 'Session not found' };
    if (!canActAsRoomHost(snap.data()?.hostUid, actingUid, actingEmail, actingDisplayName)) {
      return { ok: false, error: 'Only the session host can clear the sprint' };
    }
    await updateDoc(ref, {
      classFlowSprint: deleteField(),
      updatedAt: serverTimestamp(),
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

export async function toggleClassFlowSprintMark(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined,
  playerUid: string,
  playerDisplayName?: string
): Promise<{ ok: boolean; error?: string }> {
  let addedMark = false;
  try {
    const ref = roomRef(sessionId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Session not found');
      const hostUid = snap.data()?.hostUid;
      const actingIsHost = canActAsRoomHost(hostUid, actingUid, actingEmail, actingDisplayName);
      const actingSelf = actingUid === playerUid;
      const sprint = parseClassFlowSprint(snap.data()?.classFlowSprint);
      if (!sprint) throw new Error('No sprint');
      if (sprint.status !== 'live' && sprint.status !== 'closed') {
        throw new Error('Sprint is not open for marking');
      }
      if (!actingIsHost) {
        if (!actingSelf) {
          throw new Error('You can only check in yourself');
        }
        if (sprint.status !== 'live') {
          throw new Error('Check-in is only allowed while the sprint window is live');
        }
      }

      const set = new Set(sprint.markedCompleteUids || []);
      const wasIn = set.has(playerUid);
      // Host can toggle marks; players can only self-check in (no uncheck).
      if (wasIn && actingIsHost) {
        set.delete(playerUid);
      } else if (!wasIn) {
        set.add(playerUid);
        addedMark = true;
      }

      tx.update(ref, {
        'classFlowSprint.markedCompleteUids': Array.from(set),
        updatedAt: serverTimestamp(),
      });
    });

    if (addedMark) {
      const g = await grantSprintRewardForSinglePlayer(
        sessionId,
        actingUid,
        actingEmail,
        actingDisplayName,
        playerUid,
        playerDisplayName || 'Player'
      );
      if (!g.ok) return { ok: false, error: g.error };
    }

    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

/**
 * Facilitator approved all of a player's sprint work: mark them complete and pay the sprint rewards.
 * Add-only, and only the caller whose transaction adds the mark pays, so several open facilitator
 * screens never pay the same player twice.
 */
export async function markClassFlowSprintCompleteAndGrant(
  sessionId: string,
  sprintId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined,
  playerUid: string,
  playerDisplayName?: string
): Promise<{ ok: boolean; granted?: boolean; error?: string }> {
  try {
    const ref = roomRef(sessionId);
    const sameSprint = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Session not found');
      if (!canActAsRoomHost(snap.data()?.hostUid, actingUid, actingEmail, actingDisplayName)) {
        throw new Error('Only the session host can mark sprint work complete');
      }
      const sprint = parseClassFlowSprint(snap.data()?.classFlowSprint);
      if (!sprint || sprint.id !== sprintId) return false;
      if (!(sprint.markedCompleteUids || []).includes(playerUid)) {
        tx.update(ref, {
          'classFlowSprint.markedCompleteUids': [...(sprint.markedCompleteUids || []), playerUid],
          updatedAt: serverTimestamp(),
        });
      }
      return true;
    });
    if (!sameSprint) return { ok: true, granted: false };
    // Also covers players marked earlier whose payout never went through; the grant claims atomically.
    return await grantSprintRewardForSinglePlayer(
      sessionId,
      actingUid,
      actingEmail,
      actingDisplayName,
      playerUid,
      playerDisplayName || 'Player'
    );
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

async function bumpSessionPlayerVaultPP(sessionId: string, uid: string, delta: number): Promise<void> {
  if (delta <= 0) return;
  const ref = roomRef(sessionId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const players = [...((snap.data()?.players as { userId: string; powerPoints?: number }[]) || [])];
    const idx = players.findIndex((p) => p && (p as { userId?: string }).userId === uid);
    if (idx < 0) return;
    const p = { ...players[idx] } as { userId: string; powerPoints?: number };
    p.powerPoints = Math.max(0, (p.powerPoints ?? 0) + delta);
    players[idx] = p;
    tx.update(ref, { players, updatedAt: serverTimestamp() });
  });
}

/** Adjust session row PP (negative delta = deduction, floored at 0). */
async function bumpSessionPlayerVaultPPDelta(sessionId: string, uid: string, delta: number): Promise<void> {
  if (delta === 0) return;
  const ref = roomRef(sessionId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const players = [...((snap.data()?.players as { userId: string; powerPoints?: number }[]) || [])];
    const idx = players.findIndex((p) => p && (p as { userId?: string }).userId === uid);
    if (idx < 0) return;
    const p = { ...players[idx] } as { userId: string; powerPoints?: number };
    p.powerPoints = Math.max(0, (p.powerPoints ?? 0) + delta);
    players[idx] = p;
    tx.update(ref, { players, updatedAt: serverTimestamp() });
  });
}

async function deductVaultPPFromPlayerClamped(playerUid: string, penalty: number): Promise<void> {
  if (penalty <= 0) return;
  await applyPlayerPPDelta(playerUid, -penalty, {
    mode: 'award',
    meta: { sourceType: 'liveEvent', sourceId: 'live-event-sprint-penalty', notes: 'Live Event sprint penalty' },
  });
}

type SessionPlayerRow = {
  userId: string;
  displayName?: string;
  powerPoints?: number;
  participationCount?: number;
  movesEarned?: number;
  level?: number;
  hp?: number;
  maxHp?: number;
  shield?: number;
  maxShield?: number;
  isTeacher?: boolean;
  isReady?: boolean;
};

/**
 * Ensure a class-roster student exists on `players[]` before sprint grants.
 * Paper / offline formative: host marks complete without the student joining the Live Event.
 */
async function ensureSessionPlayerRowForOfflineGrant(
  sessionId: string,
  uid: string,
  displayName: string
): Promise<void> {
  const ref = roomRef(sessionId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const players = [...((snap.data()?.players as SessionPlayerRow[]) || [])];
    if (players.some((p) => p?.userId === uid)) return;
    players.push({
      userId: uid,
      displayName: displayName || 'Student',
      level: 1,
      powerPoints: 0,
      participationCount: 0,
      movesEarned: 0,
      hp: 100,
      maxHp: 100,
      shield: 100,
      maxShield: 100,
      isReady: false,
      isTeacher: false,
    });
    tx.update(ref, { players, updatedAt: serverTimestamp() });
  });
}

/** Keeps session `players[]` participation / moves in sync with sprint stats (FIGHT / BAG buttons). */
async function bumpSessionPlayerParticipationMoves(sessionId: string, uid: string, delta: number): Promise<void> {
  if (delta <= 0) return;
  const ref = roomRef(sessionId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const players = [...((snap.data()?.players as SessionPlayerRow[]) || [])];
    const idx = players.findIndex((p) => p?.userId === uid);
    if (idx < 0) return;
    const p = { ...players[idx] };
    const pc = (p.participationCount ?? 0) + delta;
    const me = (p.movesEarned ?? 0) + delta;
    players[idx] = { ...p, participationCount: pc, movesEarned: me };
    tx.update(ref, { players, updatedAt: serverTimestamp() });
  });
}

/**
 * Grant sprint rewards for one marked player (idempotent if already in rewardsGrantedUids).
 * Updates stats, session PP, participation/moves on session row, vault/XP, and battle log.
 */
export async function grantSprintRewardForSinglePlayer(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined,
  playerUid: string,
  playerDisplayName: string,
  options?: { skipBattleLog?: boolean }
): Promise<{ ok: boolean; granted?: boolean; error?: string }> {
  const ref = roomRef(sessionId);
  try {
    // Claim inside a transaction: it reads the server copy (getDoc can return a stale listener cache
    // right after the mark transaction) and adds the player to rewardsGrantedUids atomically, so
    // several facilitator screens never pay the same player twice.
    const claim = await runTransaction(db, async (tx) => {
      const s = await tx.get(ref);
      if (!s.exists()) return { error: 'Session not found' } as const;
      const sp = parseClassFlowSprint(s.data()?.classFlowSprint);
      if (!sp) return { error: 'No sprint data' } as const;
      const actingIsHost = canActAsRoomHost(s.data()?.hostUid, actingUid, actingEmail, actingDisplayName);
      const canSelfGrant = actingUid === playerUid && sp.status === 'live';
      if (!actingIsHost && !canSelfGrant) return { error: 'Only the session host can grant sprint rewards' } as const;
      if (!(sp.markedCompleteUids || []).includes(playerUid)) return { error: 'Player is not marked complete for this sprint' } as const;
      if ((sp.rewardsGrantedUids || []).includes(playerUid)) return { already: true } as const;
      tx.update(ref, { 'classFlowSprint.rewardsGrantedUids': arrayUnion(playerUid), updatedAt: serverTimestamp() });
      return { data: s.data(), sprint: sp } as const;
    });
    if ('error' in claim) return { ok: false, error: claim.error };
    if ('already' in claim) return { ok: true, granted: false };
    const roomData = claim.data;
    const sprint = claim.sprint;
    const snap = { data: () => roomData };

    const ppAmt = sprint.rewardParticipationPoints;
    const vaultPP = sprint.rewardVaultPP;
    const xpAmt = sprint.rewardXP;

    // Offline / paper formative: student may never have joined the Live Event room.
    await ensureSessionPlayerRowForOfflineGrant(sessionId, playerUid, playerDisplayName);

    const statsOk = await trackParticipation(sessionId, playerUid, ppAmt, { playerDisplayName });
    if (!statsOk) {
      // Nothing was paid yet: release the claim so the reward can be retried.
      await updateDoc(ref, { 'classFlowSprint.rewardsGrantedUids': arrayRemove(playerUid) }).catch(() => undefined);
      return { ok: false, error: 'Could not update participation stats for this player' };
    }

    // Do NOT call bumpSessionPlayerParticipationMoves here — trackParticipation already
    // updates session players[].participationCount / movesEarned / powerPoints.

    if (vaultPP > 0 || xpAmt > 0) {
      const studentRef = doc(db, 'students', playerUid);
      const userRef = doc(db, 'users', playerUid);
      let accountCreditOk = false;
      try {
        if (vaultPP > 0) {
          await applyPlayerPPDelta(playerUid, vaultPP, {
            mode: 'award',
            meta: { sourceType: 'liveEvent', sourceId: sessionId, notes: 'Live Event sprint reward' },
          });
        }
        const studentUpdates: UpdateData<DocumentData> = {};
        const userUpdates: UpdateData<DocumentData> = {};
        if (xpAmt > 0) {
          studentUpdates.xp = increment(xpAmt);
          userUpdates.xp = increment(xpAmt);
        }
        if (Object.keys(studentUpdates).length > 0) {
          const studentDoc = await getDoc(studentRef);
          if (studentDoc.exists()) {
            await updateDoc(studentRef, studentUpdates);
            accountCreditOk = true;
            if (xpAmt > 0) {
              await mirrorProfileXpToProgressionSystems(playerUid, xpAmt, 'live_event_sprint');
            }
          } else {
            accountCreditOk = true;
          }
        } else {
          accountCreditOk = true;
        }
        if (Object.keys(userUpdates).length > 0) {
          try {
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) await updateDoc(userRef, userUpdates);
          } catch (userErr) {
            console.warn('[liveEventSprint] users credit failed (students ok)', userErr);
          }
        }
        if (vaultPP > 0 && accountCreditOk) {
          await bumpSessionPlayerVaultPP(sessionId, playerUid, vaultPP);
          await addVaultPpGrantedMidSessionStat(sessionId, playerUid, vaultPP);
        }
      } catch (accountErr) {
        // Host cannot write other students' docs — queue vault PP for session-end self-claim.
        console.warn(
          '[liveEventSprint] mid-session account credit failed; queuing session-end PP claim',
          accountErr
        );
        if (vaultPP > 0) {
          try {
            const statsRef = doc(db, 'inSessionRooms', sessionId, 'stats', playerUid);
            await setDoc(
              statsRef,
              {
                playerId: playerUid,
                sessionEndAccountPpPending: increment(vaultPP),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          } catch (queueErr) {
            console.error('[liveEventSprint] failed to queue sessionEndAccountPpPending', queueErr);
          }
          try {
            const { registerLiveEventPendingClaim } = await import('./liveEventPendingClaimsService');
            await registerLiveEventPendingClaim(sessionId, playerUid);
          } catch {
            /* non-fatal */
          }
        }
      }
    }

    if (!options?.skipBattleLog) {
      await updateDoc(ref, {
        battleLog: arrayUnion(
          `🏃 ${playerDisplayName} earned sprint rewards for "${sprint.title}" (${ppAmt} par. pt(s)${
            vaultPP > 0 ? `, +${vaultPP} vault PP` : ''
          }${xpAmt > 0 ? `, +${xpAmt} XP` : ''}).`
        ),
      });
    }

    try {
      const startedMs = tsToMillis(sprint.startedAt);
      if (startedMs != null) {
        await recordSprintMarkedCompleteForPlayer(playerUid, startedMs, Date.now());
      }
      await recordHabitLiveEventSprintCompletion(sessionId, playerUid, Date.now());
    } catch (_) {
      /* best-effort weekly goals + habit evidence */
    }

    const roomClassId =
      typeof snap.data()?.classId === 'string'
        ? snap.data()!.classId
        : Array.isArray(snap.data()?.classIds) && typeof snap.data()!.classIds[0] === 'string'
          ? snap.data()!.classIds[0]
          : undefined;
    const startedMsForProductivity = tsToMillis(sprint.startedAt);
    const liveEventMode =
      typeof snap.data()?.liveEventMode === 'string' ? snap.data()!.liveEventMode : undefined;
    const neutralFlowEnergyType = snap.data()?.neutralFlowEnergyType as EnergyType | undefined;
    const pointsEarned = ppAmt + vaultPP + xpAmt;
    void recordClassFlowSprintCompletion({
      sessionId,
      sprintId: sprint.id,
      userId: playerUid,
      classId: roomClassId,
      completedAtMs: Date.now(),
      sprintTitle: sprint.title,
      sprintType: 'class_flow',
      startedAtMs: startedMsForProductivity,
      liveEventMode,
      neutralFlowEnergyType,
      pointsEarned,
    });

    liveEventAwardDebug({
      eventId: truncateId(sessionId),
      eventType: 'class_flow_sprint',
      playerId: truncateId(playerUid),
      playerName: playerDisplayName,
      pointsAwarded: ppAmt + vaultPP,
      xpAwarded: xpAmt,
      artifactsAwarded: null,
      reason: `sprint_complete:${truncateId(sprint.id)}`,
    });

    return { ok: true, granted: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

export async function grantClassFlowSprintRewards(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined,
  playerNames: Map<string, string>
): Promise<{ ok: boolean; granted?: number; error?: string }> {
  try {
    const ref = roomRef(sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ok: false, error: 'Session not found' };
    if (!canActAsRoomHost(snap.data()?.hostUid, actingUid, actingEmail, actingDisplayName)) {
      return { ok: false, error: 'Only the session host can grant sprint rewards' };
    }
    const sprint = parseClassFlowSprint(snap.data()?.classFlowSprint);
    if (!sprint) return { ok: false, error: 'No sprint data' };

    const grantedSet = new Set(sprint.rewardsGrantedUids || []);
    const toGrant = (sprint.markedCompleteUids || []).filter((u) => !grantedSet.has(u));
    if (toGrant.length === 0) return { ok: true, granted: 0 };

    let count = 0;
    for (const uid of toGrant) {
      const displayName = playerNames.get(uid) || 'Player';
      const r = await grantSprintRewardForSinglePlayer(
        sessionId,
        actingUid,
        actingEmail,
        actingDisplayName,
        uid,
        displayName,
        { skipBattleLog: true }
      );
      if (r.ok && r.granted) count++;
    }

    if (count > 0) {
      const ppAmt = sprint.rewardParticipationPoints;
      const vaultPP = sprint.rewardVaultPP;
      const xpAmt = sprint.rewardXP;
      await updateDoc(ref, {
        battleLog: arrayUnion(
          `🏃 Sprint rewards granted: ${count} player(s) earned "${sprint.title}" (${ppAmt} participation pt(s) each${
            vaultPP > 0 ? `, +${vaultPP} vault PP` : ''
          }${xpAmt > 0 ? `, +${xpAmt} XP` : ''}).`
        ),
      });
    }

    return { ok: true, granted: count };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

/**
 * Deduct vault PP from every class student (when the room has a class) or every session player
 * who is not marked complete for this sprint.
 * Idempotent per player via incompletePenaltiesGrantedUids. Session host is never penalized.
 */
export async function applyClassFlowSprintIncompletePenalties(
  sessionId: string,
  actingUid: string,
  actingEmail: string | undefined,
  actingDisplayName: string | undefined
): Promise<{ ok: boolean; penalized?: number; error?: string }> {
  try {
    const ref = roomRef(sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ok: false, error: 'Session not found' };
    if (!canActAsRoomHost(snap.data()?.hostUid, actingUid, actingEmail, actingDisplayName)) {
      return { ok: false, error: 'Only the session host can apply incomplete penalties' };
    }
    const sprint = parseClassFlowSprint(snap.data()?.classFlowSprint);
    if (!sprint) return { ok: false, error: 'No sprint data' };

    const penalty = Math.max(0, Math.floor(sprint.incompletePenaltyVaultPP || 0));
    if (penalty <= 0) return { ok: false, error: 'This sprint has no incomplete PP penalty configured' };

    const roomHostUid = typeof snap.data()?.hostUid === 'string' ? snap.data()!.hostUid : '';
    const marked = new Set(sprint.markedCompleteUids || []);
    const already = new Set(sprint.incompletePenaltiesGrantedUids || []);
    const sessionPlayers = (snap.data()?.players as { userId?: string }[]) || [];
    const roomClassId =
      typeof snap.data()?.classId === 'string'
        ? snap.data()!.classId
        : Array.isArray(snap.data()?.classIds) && typeof snap.data()!.classIds[0] === 'string'
          ? snap.data()!.classIds[0]
          : undefined;

    let rosterUids: string[] = [];
    if (roomClassId) {
      try {
        const classUids = await fetchClassroomStudentUids(roomClassId);
        if (classUids.length > 0) rosterUids = classUids;
      } catch {
        /* fall back to session roster */
      }
    }
    if (rosterUids.length === 0) {
      rosterUids = sessionPlayers
        .map((row) => (typeof row?.userId === 'string' ? row.userId : ''))
        .filter(Boolean);
    }

    const toPenalize: string[] = [];
    for (const uid of rosterUids) {
      if (!uid || uid === roomHostUid) continue;
      if (marked.has(uid)) continue;
      if (already.has(uid)) continue;
      toPenalize.push(uid);
    }

    if (toPenalize.length === 0) return { ok: true, penalized: 0 };

    const newlyPenalized: string[] = [];
    for (const uid of toPenalize) {
      try {
        await deductVaultPPFromPlayerClamped(uid, penalty);
        await bumpSessionPlayerVaultPPDelta(sessionId, uid, -penalty);
        newlyPenalized.push(uid);
      } catch (err) {
        console.warn('[applyClassFlowSprintIncompletePenalties] failed for', uid, err);
      }
    }

    if (newlyPenalized.length === 0) {
      return { ok: false, error: 'Could not apply penalties (check player documents / vaults)' };
    }

    await updateDoc(ref, {
      'classFlowSprint.incompletePenaltiesGrantedUids': arrayUnion(...newlyPenalized),
      updatedAt: serverTimestamp(),
      battleLog: arrayUnion(
        `⚠️ Incomplete sprint penalty: −${penalty} vault PP for ${newlyPenalized.length} player(s) on "${sprint.title}" (not marked complete).`
      ),
    });

    const nowMs = Date.now();
    for (const uid of newlyPenalized) {
      void recordClassFlowSprintMissed({
        sessionId,
        sprintId: sprint.id,
        userId: uid,
        classId: roomClassId,
        missedAtMs: nowMs,
        sprintTitle: sprint.title,
      });
    }

    return { ok: true, penalized: newlyPenalized.length };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}
